import { useEffect, useState } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import {
  Checkbox,
  FormControl, MenuItem, Select,
} from '@mui/material';
import {
  attachUserPageMentorsBulk,
} from '@containers/Admin/containers/UserPage/actions';
import { Mentor } from './types';
import { SELECTOR_WIDTH } from './consts';

interface SelectMentorsProps {
  isMentor: boolean
}

/**
* Select for mentors
* @param {boolean} isMentor - Value for checking current user is mentor
* @returns {React.ReactElement} React element
*/
export const SelectMentors = ({ isMentor }: SelectMentorsProps) => {
  const dispatch = useDispatch();
  const {
    userMentors,
    allMentors,
    userData: { id },
  } = useSelector((state: RootStateOrAny) => state.get('userPage').toJS()); // FIXME replace to typing store

  const [options, setOptions] = useState<Array<Mentor>>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialSelectedMentors, setInitialSelectedMentors] = useState<Array<string>>([]);

  const checkedHandler = (option: Mentor): void => {
    setOptions((prevOptions) => prevOptions.map((prevOption) => (
      prevOption.value === option.value
        ? { ...prevOption, checked: !prevOption.checked }
        : prevOption
    )));
  };

  const closeHandler = (): void => {
    const selectedMentors = options
      .filter((option) => option.checked)
      .map((option) => option.value);

    const selectedSet = new Set(selectedMentors);
    const initialSet = new Set(initialSelectedMentors);

    const mentorsToAdd = selectedMentors
      .filter((mentorId) => !initialSet.has(mentorId))
      .map((mentorId) => ({ mentorId, isRemove: false }));
    const mentorsToRemove = initialSelectedMentors
      .filter((mentorId) => !selectedSet.has(mentorId))
      .map((mentorId) => ({ mentorId, isRemove: true }));
    const changes = [...mentorsToAdd, ...mentorsToRemove];

    if (changes.length > 0) {
      dispatch(attachUserPageMentorsBulk(id, changes));
    }

    setIsOpen(false);
  };

  const renderValueHandler = ():string => options
    .filter((option) => option.checked)
    .map((option) => option.label)
    .join(', ');

  /**
  * Add field "checked" to each mentors
  * @returns {Array<Mentor>} Mentors with changed field "checked"
  */
  const checkUserMentorsInAllMentors = (): void => {
    const optionsToSet: Array<Mentor> = [];
    const userMentorsIds = userMentors.map(({ value }: Mentor) => value);
    allMentors.forEach(({ value, label }: Mentor) => {
      optionsToSet.push({ value, label, checked: !!userMentorsIds.includes(value) });
    });
    setOptions(optionsToSet);
  };

  useEffect(() => {
    if (isOpen) {
      return;
    }

    checkUserMentorsInAllMentors();
  }, [userMentors, allMentors]);

  if (isMentor) {
    return userMentors.length
      ? userMentors.map(({ label, value }: Mentor) => <div key={value}>{label}</div>)
      : 'No mentors';
  }
  return (
    <div>
      <FormControl sx={{ width: SELECTOR_WIDTH }} size="small">
        <Select
          multiple
          value={options.filter((option) => option.checked).map((option) => option.value)}
          open={isOpen}
          onOpen={() => {
            setInitialSelectedMentors(
              options
                .filter((option) => option.checked)
                .map((option) => option.value),
            );
            setIsOpen(true);
          }}
          onClose={closeHandler}
          renderValue={() => renderValueHandler()}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} selected={option.checked}>
              <Checkbox checked={option.checked} onChange={() => checkedHandler(option)} />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
